/**
 * What we CALL two-factor authentication on screen — decided in one place, because the right name
 * depends on who is reading.
 *
 * ADVISERS AND FIRM STAFF get "multi-factor authentication". It is the term on cyber-insurance
 * proposals, licensee audits and due-diligence questionnaires, so to a professional it reads as the
 * control they are already required to have, named properly. "Two-step sign in" would read as
 * something lesser, and would not obviously be the box they need to tick on a compliance form.
 *
 * CONSUMERS get "two-step sign in". Someone planning their mother's aged care has never met the
 * phrase "multi-factor authentication" and should not have to: the words have to do the explaining,
 * because there is nobody beside them to do it. This is the same reason the setup screen says "scan
 * this square" rather than "scan the QR code to provision your TOTP authenticator".
 *
 * Same feature, same screens, same security. Only the label changes, and it changes because the
 * audience does.
 */

export interface TwoFactorWording {
    /** Title case, for headings. */
    title: string;
    /** Mid-sentence, lower case. */
    inline: string;
    setUp: string;
    turnOn: string;
    turnOff: string;
    isOn: string;
    isOff: string;
}

const PROFESSIONAL: TwoFactorWording = {
    title: 'Multi-factor authentication',
    inline: 'multi-factor authentication',
    setUp: 'Set up multi-factor authentication',
    turnOn: 'Turn on multi-factor authentication',
    turnOff: 'Turn off multi-factor authentication',
    isOn: 'Multi-factor authentication is on',
    isOff: 'Multi-factor authentication is off',
};

const CONSUMER: TwoFactorWording = {
    title: 'Two-step sign in',
    inline: 'two-step sign in',
    setUp: 'Set up two-step sign in',
    turnOn: 'Turn on two-step sign in',
    turnOff: 'Turn off two-step sign in',
    isOn: 'Two-step sign in is on',
    isOff: 'Two-step sign in is off',
};

/**
 * `professional` means an adviser, firm staff member or platform admin — the same people for whom a
 * second factor is compulsory, which is not a coincidence: the population that must have it is the
 * population that already knows what it is called.
 */
export function twoFactorWording(professional: boolean): TwoFactorWording {
    return professional ? PROFESSIONAL : CONSUMER;
}
